// src/lib/demoBrand.js — the trailer's fictional company, as a LABEL overlay.
//
// docs/specs/DEMO-ENVIRONMENT-v0.1.md §0 / §5. `company_id` stays
// 'chatrone_us' in the demo database — it is hardcoded in 101 files and is the
// partition key, not a display value. Only what a visitor READS is rebranded,
// and that lives in two i18n trees (src/i18n and src/locales) plus a handful of
// display sites that never went through i18n. This module is the one place the
// Meridian names are spelled.
//
// Pure: no env, no imports. `src/lib/demoClient.js` binds it to VITE_DEMO_MODE
// for the browser; tests call it with an explicit flag.

export const DEMO_COMPANY = 'Meridian Pictures Group'
export const DEMO_PRINCIPAL_EMAIL = 'nora@meridianpictures.example'
export const DEMO_PRINCIPAL_NAME = 'Nora Ellison'

// Exact production strings → their Meridian reading. Value-based on purpose:
// the two i18n trees key the same label under different paths
// (entities.chatroneUs vs entities.chatrone_us), and a value map covers both
// without either tree knowing this file exists.
export const DEMO_LABELS = {
  'Chatrone US':                'Meridian Pictures',
  'Chatrone LatAm':             'Meridian Latin America',
  'Chatrone Latin America':     'Meridian Latin America',
  'Chatrone Management':        'Meridian Management',
  'Chatrone Production':        'Meridian Productions',
  'Chatrone Production (US)':   'Meridian Productions (US)',
  'Chatrone Production, LLC':   'Meridian Productions, LLC',
  'Chatrone, LLC':              'Meridian Pictures, LLC',
}

/** The demo reading of a display string; unchanged when not in demo mode or
 *  when the string is not an entity label. "Chatrone OS" is the PRODUCT and is
 *  deliberately absent from the map — the trailer demonstrates Chatrone OS. */
export function demoLabel(text, isDemo) {
  if (!isDemo || typeof text !== 'string') return text
  return Object.prototype.hasOwnProperty.call(DEMO_LABELS, text) ? DEMO_LABELS[text] : text
}

/** The label for a company_id partition key. Used by the display sites that
 *  branch on company_id inline rather than through i18n. */
export function entityLabelFor(companyId, isDemo, { long = false } = {}) {
  const latam = companyId === 'chatrone_latam'
  const base = latam ? (long ? 'Chatrone Latin America' : 'Chatrone LatAm') : 'Chatrone US'
  return demoLabel(base, isDemo)
}

/** Prose that names the company mid-sentence ("needs a Chatrone response",
 *  "a Chatrone move is outstanding") — the word, not an exact label. "Chatrone
 *  OS" is left alone: the product keeps its name. */
export function demoProse(text, isDemo) {
  if (!isDemo || typeof text !== 'string') return text
  return text.replace(/\bChatrone\b(?!\s*OS\b)/g, 'Meridian')
}

/** Deep-copy any JSON tree with demoProse applied to every string. Same
 *  object back outside the demo. */
export function demoProseTree(tree, isDemo) {
  if (!isDemo) return tree
  const walk = (node) => {
    if (typeof node === 'string') return demoProse(node, true)
    if (Array.isArray(node)) return node.map(walk)
    if (node && typeof node === 'object') { const out = {}; for (const [k, v] of Object.entries(node)) out[k] = walk(v); return out }
    return node
  }
  return walk(tree)
}

/** Deep-copy a translations tree with every entity label rewritten. Returns
 *  the SAME object when not in demo mode, so production pays nothing. */
export function applyDemoLabels(tree, isDemo) {
  if (!isDemo) return tree
  const walk = (node) => {
    if (typeof node === 'string') return demoLabel(node, true)
    if (Array.isArray(node)) return node.map(walk)
    if (node && typeof node === 'object') {
      const out = {}
      for (const [k, v] of Object.entries(node)) out[k] = walk(v)
      return out
    }
    return node
  }
  return walk(tree)
}
